import React from 'react'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'

const HospitalPortal = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">What the hospital portal is for</Typography>
      <Typography>
        The hospital booking portal gives organisation users a focused PTS
        workspace outside the main AmbuNet staff interface. Portal users can
        request transport, review planned work, track journeys for their ward or
        organisation, and contact the control room when live support is
        available.
      </Typography>
      <Typography>
        Portal users are created from the organisation record in{' '}
        <DocLink url="/pts/organisations" text="PTS Organisations" />.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Access levels</Typography>
      <Typography>
        Each portal user belongs to one PTS organisation and has one of three
        access levels:
      </Typography>
      <ul style={{ marginTop: 0, lineHeight: 2 }}>
        <li>
          <strong>Ward</strong> users can see and request work for their
          assigned wards only — their menu covers the ward dashboard,
          requesting transport, their own requests, ward journeys, and Help
          &amp; Support.
        </li>
        <li>
          <strong>Site</strong> users see organisation-level activity across
          the hospital site, plus Quotes, Invoicing, all wards, reports —
          and <strong>Portal Users</strong>: Site is the level that can
          create and manage the organisation's other portal users (newly
          invited users always start at Ward level).
        </li>
        <li>
          <strong>Admin</strong> users have the broadest oversight view —
          bookings, journeys, quotes, invoicing, and reporting for the whole
          organisation.
        </li>
      </ul>
      <Alert severity="warning" variant="outlined">
        If a Ward access user has no wards assigned, they cannot see booking or
        journey data. AmbuNet shows an access message instead, while still
        keeping Help and Support available.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Dashboard views</Typography>
      <Typography>
        Ward users start from a ward dashboard and can switch between their
        permitted wards. Site and Admin users can use wider organisation views
        and ward filters. The portal does not create a default ward label; it
        only shows real wards and departments configured for the organisation.
      </Typography>
      <Typography>
        Planned work includes upcoming and recently completed journeys for the
        selected ward or organisation scope. My Requests shows bookings created
        by the current portal user, while wider views show the journeys the
        user's access level permits.
      </Typography>
      <Typography>
        Journey lists also show live crew-tracking chips — <strong>Crew
        assigned</strong>, <strong>Crew en route</strong> with an estimated
        minutes-away figure, and <strong>Crew at hospital</strong>. The ETA is
        computed from the vehicle's live position while the crew is sharing
        their location (positions fresher than five minutes only), so a
        missing ETA means no live location, not a missing crew.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Creating requests</Typography>
      <Typography>
        <strong>New Transport Request</strong> opens the same booking form
        internal coordinators use — the same journey types, customer-aware
        pickup/dropoff options, per-stop hospital selection, return journeys,
        and recurrence — with the organisation and requester section replaced
        by a read-only "Requesting Hospital" panel (the requesting ward is
        selectable when the user has more than one). The booking's source is
        recorded as Hospital Portal, and the submitting user is stored so
        coordinators can see who requested it inside AmbuNet.
      </Typography>
      <Typography>
        Patient lookup on the portal is deliberately stricter than the
        internal one: results are limited to patients already linked to the
        organisation (by hospital number or a previous booking), the search
        needs specific combinations such as hospital number + surname or
        date of birth, NHS number + surname or date of birth, or surname +
        date of birth + postcode, and every search is written to the patient
        access log.
      </Typography>
      <Typography>
        For internal coordinators creating a booking on behalf of a hospital,
        the Requested By list is filtered to relevant portal users. Ward access
        users appear when they are assigned to the selected ward. Site and Admin
        users appear for the whole organisation.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">
        Booking onto a dedicated crew
      </Typography>
      <Typography>
        Where the transport provider has set up a duty dedicated to the
        organisation (see{' '}
        <DocLink url="/patient-transport/planning" text="Planning" docsPage />
        ), the portal's request form shows a <strong>Dedicated Crew</strong>{' '}
        section once the pickup time is set. A request covered by a dedicated
        crew books <strong>straight onto that crew and is approved
        automatically</strong> — no coordinator review step.
      </Typography>
      <ul style={{ marginTop: 0, lineHeight: 2 }}>
        <li>
          <strong>One eligible crew</strong> — it's selected automatically,
          with the crew's duty hours and how many journeys they already have
          booked shown.
        </li>
        <li>
          <strong>Several eligible crews</strong> — the portal user chooses
          who takes the journey; leaving the choice blank sends the request
          for review instead of booking it directly.
        </li>
        <li>
          <strong>No crew on duty at that time</strong> — a warning explains
          the request will go to the transport provider for review as a
          normal request.
        </li>
      </ul>
      <Alert severity="info" variant="outlined">
        For Discharge journeys the section always appears (including the
        "no crew on duty" warning). For every other journey type it only
        appears when a dedicated crew is actually on duty, so hospitals
        without one aren't warned about it on every booking.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Risk assessments in the portal</Typography>
      <Typography>
        Where a patient has risk assessments marked{' '}
        <strong>Visible in hospital portal</strong>, portal users can open
        them from the journey list's Risk Assessments column. Assessments not
        marked hospital-visible, or linked to a different organisation, never
        appear — and every portal read is written to the patient's access
        log. See{' '}
        <DocLink
          url="/patient-transport/risk-assessments"
          text="Risk Assessments"
          docsPage
        />
        .
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Previewing the portal</Typography>
      <Typography>
        Coordinators can open a read-through preview of any organisation's
        portal — with a Ward / Site / Admin view switcher — from the{' '}
        <strong>Portal Preview</strong> action on{' '}
        <DocLink url="/pts/organisations" text="PTS Organisations" />. Use it
        to see exactly what a hospital user is describing before walking
        them through it.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Accepting the portal terms</Typography>
      <Typography>
        Before a portal user can see any booking or journey data, they must
        accept the current PTS portal terms of use. This is a one-time step
        per terms version — if the terms are ever updated, users are asked to
        accept again next time they log in.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Quotes and invoices</Typography>
      <Typography>
        Site and Admin portal users have Quotes and Invoices tabs for their
        organisation. Quotes appear once a coordinator has sent one, with an{' '}
        <strong>Accept</strong> action that moves the booking into planning —
        Ward-level users do not see pricing. Invoices show everything billed
        to the organisation once it has left Draft status, with running
        totals and any credit notes applied.
      </Typography>
      <Typography>
        Eligibility screening, where enabled for your tenant, appears as a
        short questionnaire on the booking-creation form itself — see{' '}
        <DocLink
          url="/patient-transport/bookings"
          text="Bookings"
          docsPage
        />
        .
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Help and Support chat</Typography>
      <Typography>
        Portal users can start a control room chat only when a dispatcher is
        currently active on the PTS dispatch screen and available to receive the
        request. If no dispatcher is available, the portal keeps the chat action
        disabled and explains that live support is offline.
      </Typography>
      <Typography>
        When a portal user asks to chat, dispatch sees the request on{' '}
        <DocLink url="/pts/control" text="PTS Control" />. A dispatcher accepts
        the request to open the conversation, then both sides can exchange
        messages from their respective chat windows.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Common setup checks</Typography>
      <ul style={{ marginTop: 0, lineHeight: 2 }}>
        <li>The user must be linked to the correct PTS organisation.</li>
        <li>Ward users must have at least one active permitted ward.</li>
        <li>The organisation should have wards or departments configured before
          ward-level access is issued.</li>
        <li>The user must complete the set-password email before logging in.</li>
        <li>Live chat requires an active dispatcher on the dispatch screen.</li>
      </ul>
    </Stack>
  </Stack>
)

export default HospitalPortal
